// The wire contract between a client and its Room Durable Object. Both the
// web client and the Room DO import these types, so a shape change fails to
// typecheck on both sides instead of drifting silently.

import type { AssistantHostState, AssistantPublicState, Agenda } from "./assistant.js";
import type { PanelItem, PanelList, PanelState, PrivateData, SuggestionAudience, SuggestionItem } from "./panel.js";
import type { ListenLine } from "./listen.js";
import type { ChatMessage } from "./chat.js";
import type { LateCall, LateGuestView } from "./lateGuest.js";

export type ParticipantKind = "host" | "guest";
export type ConnectionState = "connected" | "reconnecting";

export interface TrackRef {
  /** The Cloudflare Realtime session id that published this track. */
  readonly sessionId: string;
  readonly trackName: string;
}

export interface ParticipantTracks {
  readonly audio?: TrackRef;
  readonly video?: TrackRef;
  readonly screen?: TrackRef;
}

export interface ParticipantSnapshot {
  readonly id: string;
  readonly name: string;
  readonly kind: ParticipantKind;
  readonly muted: boolean;
  readonly cameraOff: boolean;
  readonly sharingScreen: boolean;
  readonly originalSound: boolean;
  /** When this person raised their hand (epoch ms), or absent when it is down.
   * Hands are shown in the order they went up. Absent from an older Worker. */
  readonly handRaisedAt?: number;
  readonly connectionState: ConnectionState;
  /** Still in the call with Breeze in the background (the app sent to the
   * home screen, the tab hidden): their camera has stopped, and the tile
   * says they are still there. Absent from an older Worker or client. */
  readonly away?: boolean;
  readonly tracks: ParticipantTracks;
  /** Present on a phone line dialed into the room (a direct Breeze call):
   * its call id. The host hangs it up with POST /api/cadence/calls/:id/cancel.
   * Absent from an older Worker. */
  readonly phoneCall?: string;
}

export interface RoomSnapshot {
  readonly roomId: string;
  readonly recording: boolean;
  /** The recording in progress, so the host can offer to delete it the
   * moment it stops. Absent from an older Worker. */
  readonly recordingId?: string | null;
  /** To the host only: the recording is running and the room has not been
   * told (the operator's quiet default). `recording` is false for everyone,
   * the host included, until the host taps Record. */
  readonly recordingQuiet?: boolean;
  /** The owner joined with Record on and recording has not started yet: it
   * starts once two people are in and sending media. Absent from an older
   * Worker. */
  readonly recordArmed?: boolean;
  readonly knockRequired: boolean;
  readonly participants: readonly ParticipantSnapshot[];
}

/** `GET /api/rooms/:kind/:slug/info`: what the pre-join screen reads before
 * anyone connects. A personal room carries its owner's display name (the
 * guest screen's "<name>'s call") and whether the owner is in right now
 * (a guest who knocks while they are not waits for them). */
export interface RoomInfo extends RoomSnapshot {
  readonly ownerName?: string;
  readonly ownerPresent: boolean;
  /** Only when the request carried a user key (`Authorization: Bearer`):
   * whether that account has a seat in this call right now, on any device
   * (a seat inside its reconnect grace window counts). */
  readonly selfPresent?: boolean;
  /** With `selfPresent`, when the request also sent `?deviceId=`: whether
   * that seat is on this same device. A seat that is present and not here
   * is on another device, and joining with `move=1` takes it over. */
  readonly selfHere?: boolean;
}

/** WebSocket close codes the Room uses. A client reconnects on anything
 * else; on these it ends the call screen instead. */
export const ROOM_CLOSE_CODES = {
  /** The client said `leave`. */
  left: 4000,
  /** The same device (or the same rejoin token) connected again. */
  replaced: 4001,
  /** The month's usage cap refused a new call. */
  capReached: 4002,
  /** The same account took this seat over from another device (a join with
   * `move=1`). Preceded by a `movedAway` message. */
  movedAway: 4003,
  /** The call was ended for everyone in the room (an Auto Breeze call that
   * hung up while its owner was listening). Do not reconnect. */
  ended: 4005,
  /** The Room closed this socket itself: it stopped pinging and the network
   * never sent a close frame, so nothing would otherwise have told the room
   * it was gone. Not in the "do not reconnect" set above — a client that is
   * actually still alive just reconnects, the same as any other drop. */
  stale: 4006,
} as const;

/** `GET /api/me/live`: the calls the key's account has a seat in right now. */
export interface LiveCall {
  readonly kind: "personal" | "once";
  readonly slug: string;
  /** The call's join link. */
  readonly url: string;
  readonly started: string;
  /** Owned by this account (its personal room, or a one-off it made). */
  readonly own: boolean;
  /** Someone besides this account is in the room too: the meeting has actually begun, not just been joined. */
  readonly othersPresent: boolean;
}

export interface LiveCallsResponse {
  readonly calls: readonly LiveCall[];
}

/** One door trivia question a waiting guest answered. */
export interface KnockAnswer {
  readonly q: string;
  readonly right: boolean;
}

export interface KnockEntry {
  readonly knockId: string;
  readonly name: string;
  readonly requestedAt: number;
  /** The door trivia they have answered so far, oldest first. Sent only to
   * hosts; the room re-sends the knock with each new answer. */
  readonly answers?: readonly KnockAnswer[];
}

// ---- Client -> Room ---------------------------------------------------

export interface JoinMessage {
  readonly type: "join";
  /** Stable per-device id, persisted in local storage, so a reconnect from
   * the same device is recognized rather than treated as a new person. */
  readonly deviceId: string;
  readonly name: string;
  /** Set when the client already holds a rejoin token from an earlier
   * connection to this same room, so the DO can restore its participant
   * record instead of creating a new one. */
  readonly rejoinToken?: string;
}

export interface PublishTrackMessage {
  readonly type: "publishTrack";
  readonly kind: "audio" | "video" | "screen";
  readonly sessionId: string;
  readonly trackName: string;
  /** Audio only: the original-sound state this track was published with.
   * Toggling original sound republishes the audio track (the SFU accepts no
   * client offer on /renegotiate, so a fresh push is the one way to change
   * what the far side is told), and carrying the flag here lets the room
   * send ONE participantUpdated with the new track and the new flag
   * together, so nobody pulls the new track as mono and then re-pulls it. */
  readonly originalSound?: boolean;
}

export interface UnpublishTrackMessage {
  readonly type: "unpublishTrack";
  readonly kind: "audio" | "video" | "screen";
}

export interface UpdateStateMessage {
  readonly type: "updateState";
  readonly muted?: boolean;
  readonly cameraOff?: boolean;
  readonly originalSound?: boolean;
  readonly away?: boolean;
}

/** Raise or lower a hand. Without `participantId` it is the sender's own.
 * With one, only the host may act, and only to lower (a raise for someone
 * else is ignored). The room answers with a `participantUpdated`. */
export interface HandSetMessage {
  readonly type: "handSet";
  readonly raised: boolean;
  readonly participantId?: string;
}

export interface KnockRespondMessage {
  readonly type: "knockRespond";
  readonly knockId: string;
  readonly allow: boolean;
}

/** Host-only: turn recording on or off (BRIEF 9/19 — off by default, one
 * tap turns it on). `RecordingStateMessage` below is the outbound broadcast
 * this produces once the Room DO has actually confirmed it with the
 * recorder; a guest sending this is silently ignored, same as a guest
 * sending `knockRespond`. */
export interface SetRecordingMessage {
  readonly type: "setRecording";
  readonly recording: boolean;
}

/** Host-only: throw away a recording just stopped (the trash button on the
 * "Recording saved" popup). The Room marks it, the upload is deleted when it
 * lands, and a copy already filed is deleted with its files. Nothing is sent
 * to the owner's webhook. */
export interface DiscardRecordingMessage {
  readonly type: "discardRecording";
  readonly recordingId: string;
}

/** Anyone in the call: add a note, question, or next step to the shared
 * panel. `owner` is for next steps; `goal` marks a note as the author's own
 * goal for the call (the waiting-room prompt, shared on purpose). Refused with
 * an `error` of code `panel` when a field is invalid or a limit is reached. */
export interface PanelAddMessage {
  readonly type: "panelAdd";
  readonly list: PanelList;
  readonly text: string;
  readonly owner?: string;
  readonly goal?: boolean;
}

/** Edit a shared item. The author or the host may change `text`, `owner`, or
 * `done`; only the host may set `asked` on a question. */
export interface PanelEditMessage {
  readonly type: "panelEdit";
  readonly list: PanelList;
  readonly id: string;
  readonly text?: string;
  readonly owner?: string;
  readonly done?: boolean;
  readonly asked?: boolean;
}

/** Delete a shared item: the author or the host. */
export interface PanelDeleteMessage {
  readonly type: "panelDelete";
  readonly list: PanelList;
  readonly id: string;
}

/** Send one chat message. `id` is made by the sender, so a resend is stored once. */
export interface ChatSendMessage {
  readonly type: "chatSend";
  readonly id: string;
  readonly text: string;
}

/** Delete a chat message: its author, or the host. */
export interface ChatDeleteMessage {
  readonly type: "chatDelete";
  readonly id: string;
}

/** Ask for this person's private data back. `token` is a random secret the
 * browser keeps; a signed-in account is keyed by account id instead and the
 * token is only a fallback. Allowed while still waiting to be let in. */
export interface PrivateGetMessage {
  readonly type: "privateGet";
  readonly token: string;
}

/** Replace this person's private data. Never broadcast, never in a snapshot. */
export interface PrivateSetMessage {
  readonly type: "privateSet";
  readonly token: string;
  readonly data: PrivateData;
}

/** The host: turn the live assistant on or off for this call. It turns on
 * by itself when the host joins, and a host's off holds for the rest of that
 * call. Anyone else is refused with an `error` of code `assistant_denied`. */
export interface AssistantSetMessage {
  readonly type: "assistantSet";
  readonly on: boolean;
}

/** The host: whether guests may chat with Breeze, whether Breeze
 * suggests questions from guests' own goals, and whether guests may use the
 * shared notes, questions, and next steps. All start on. A field left
 * out is left as it was. Anyone else is refused like `assistantSet`. */
export interface AssistantOptionsMessage {
  readonly type: "assistantOptions";
  readonly guestChat?: boolean;
  readonly guestGoals?: boolean;
  readonly guestNotes?: boolean;
}

/** Anyone with a seat: a private question for Breeze. `id` is made by the
 * client, so sending the same ask again after a drop is answered once and
 * counted once. `text` is 1 to 1000 characters. The answer comes back to this
 * seat only, as `assistantAnswer`. */
export interface AssistantAskMessage {
  readonly type: "assistantAsk";
  readonly id: string;
  readonly text: string;
}

/** The host: the agenda they pasted when none was found. Held in the Room's
 * memory for this call only, and only while the assistant is on. */
export interface AssistantAgendaMessage {
  readonly type: "assistantAgenda";
  readonly text: string;
}

/** Test rooms only (the throwaway accounts' own rooms, and only with the
 * test key): text stands in for speech, a stub stands in for the model, and
 * a figure stands in for spend. Refused everywhere else. */
export interface AssistantTestMessage {
  readonly type: "assistantTest";
  readonly key: string;
  readonly action: "line" | "model" | "spend" | "tick" | "agenda" | "reset" | "peek" | "record" | "quiet";
  /** `record`, `quiet`: stand in for the recorder (announced, or quiet) starting (true) or stopping (false). */
  readonly record?: boolean;
  /** `line`: the participant speaking, as an id. */
  readonly speakerId?: string;
  readonly text?: string;
  /** `model`: a canned model reply (a JSON string), or empty to use the real model. */
  readonly reply?: string;
  /** `spend`: dollars to add to the call's spend. */
  readonly usd?: number;
  /** `agenda`: a canned agenda the source would have returned. */
  readonly agenda?: Agenda;
}

/** The host only: send the late-guest nudge now, through whatever channel the
 * host and that guest last wrote on. The wording is the Room's own, never the
 * client's; the Room refuses it from anyone but the proven owner. */
export interface LateNudgeSendMessage {
  readonly type: "lateNudgeSend";
  readonly callId: string;
}

export interface LeaveMessage {
  readonly type: "leave";
}

/** Test rooms only (a throwaway account's own room, with the test key; refused
 * everywhere else): stands in for the Seamless booked-calls list, answers the
 * nudge POST in place of Seamless and records what would have been sent, so
 * the late-guest watcher can be exercised without a real guest or booking. */
export interface LateTestMessage {
  readonly type: "lateTest";
  readonly key: string;
  readonly action: "calls" | "sink" | "peek" | "reset";
  /** `calls`: the booked calls the watcher reads, or null to stop faking them. */
  readonly calls?: readonly LateCall[] | null;
  /** `sink`: what the stand-in for Seamless answers to a nudge. */
  readonly answer?: "ok" | "fail" | "missing" | "throw" | "off";
}

export interface PingMessage {
  readonly type: "ping";
}

export type ClientMessage =
  | LateNudgeSendMessage
  | LateTestMessage
  | JoinMessage
  | PublishTrackMessage
  | UnpublishTrackMessage
  | UpdateStateMessage
  | HandSetMessage
  | KnockRespondMessage
  | SetRecordingMessage
  | DiscardRecordingMessage
  | PanelAddMessage
  | PanelEditMessage
  | PanelDeleteMessage
  | ChatSendMessage
  | ChatDeleteMessage
  | PrivateGetMessage
  | PrivateSetMessage
  | AssistantSetMessage
  | AssistantOptionsMessage
  | AssistantAskMessage
  | AssistantAgendaMessage
  | AssistantTestMessage
  | LeaveMessage
  | PingMessage;

// ---- Room -> Client ----------------------------------------------------

export interface WelcomeMessage {
  readonly type: "welcome";
  readonly selfId: string;
  readonly rejoinToken: string;
  readonly room: RoomSnapshot;
}

export interface WaitingMessage {
  readonly type: "waiting";
  readonly knockId: string;
  /** The live assistant is on for this call, so the waiting room says so. */
  readonly assistantOn?: boolean;
}

export interface KnockWaitingMessage {
  readonly type: "knockWaiting";
  readonly knock: KnockEntry;
}

/** A knock that stopped waiting (they closed the page, or were replaced by
 * the same device knocking again). Hosts drop it from their knock list. */
export interface KnockCancelledMessage {
  readonly type: "knockCancelled";
  readonly knockId: string;
}

export interface KnockResultMessage {
  readonly type: "knockResult";
  readonly allowed: boolean;
}

export interface ParticipantJoinedMessage {
  readonly type: "participantJoined";
  readonly participant: ParticipantSnapshot;
}

export interface ParticipantLeftMessage {
  readonly type: "participantLeft";
  readonly participantId: string;
}

export interface ParticipantUpdatedMessage {
  readonly type: "participantUpdated";
  readonly participant: ParticipantSnapshot;
}

export interface RecordingStateMessage {
  readonly type: "recordingState";
  readonly recording: boolean;
  /** The recording this is about, on both the start and the stop. */
  readonly recordingId?: string;
  /** Sent to the host alone: the room has not been told about this one. */
  readonly quiet?: boolean;
}

/** To the host: the recording they asked to delete is gone (or will be the
 * moment its upload lands). */
export interface RecordingDiscardedMessage {
  readonly type: "recordingDiscarded";
  readonly recordingId: string;
}

/** To an approved socket, right after `welcome` and on a resync: the whole
 * shared panel. Never part of `RoomSnapshot`, because the snapshot is also
 * served unauthenticated by the room info route. */
export interface PanelStateMessage {
  readonly type: "panelState";
  readonly panel: PanelState;
}

/** To every approved socket: a shared item was added or changed. */
export interface PanelItemMessage {
  readonly type: "panelItem";
  readonly list: PanelList;
  readonly item: PanelItem;
}

/** To every approved socket: a shared item was deleted. */
export interface PanelRemovedMessage {
  readonly type: "panelRemoved";
  readonly list: PanelList;
  readonly id: string;
}

/** To the asking socket only: its private data (`data` is null when the Room
 * holds nothing for that key) and when the Room last saved it. */
export interface PrivateStateMessage {
  readonly type: "privateState";
  readonly data: PrivateData | null;
  readonly updated: number;
  /** When the call in progress started (epoch ms), null when none is live. A
   * client holding its own copy from a different call knows that copy is old. */
  readonly call: number | null;
}

/** To the saving socket only: its `privateSet` is stored. */
export interface PrivateSavedMessage {
  readonly type: "privateSaved";
  readonly updated: number;
}

/** Lines the assistant offers. `host` reaches the host's sockets, `self`
 * exactly one participant's sockets. Never broadcast, never stored. */
export interface SuggestionsMessage {
  readonly type: "suggestions";
  readonly audience: SuggestionAudience;
  readonly items: readonly SuggestionItem[];
}

/** To host sockets only: Breeze changed or made one of the operator's other
 * preps on its own during the call (worker prepdoc.ts `prepWatch`). `text`
 * is one sentence saying what. Never broadcast, never stored. */
export interface PrepEditedMessage {
  readonly type: "prepEdited";
  readonly text: string;
  readonly changed: readonly string[];
  readonly created: { readonly slug: string; readonly title: string } | null;
}

/** To the proven owner's sockets only: the late-guest watcher (QUEUE row
 * 455). `late` is null when there is nothing to show (or it just went away).
 * Never broadcast, never stored, never part of a snapshot or `/info`. */
export interface LateGuestMessage {
  readonly type: "lateGuest";
  readonly late: LateGuestView | null;
}

/** To every socket, approved or still waiting: whether the assistant is on.
 * The per-person parts (`consented`) are about the socket it is sent to. */
export interface AssistantStateMessage {
  readonly type: "assistantState";
  readonly state: AssistantPublicState;
}

/** The reply to one `assistantAsk`, to the asking seat's sockets and nobody
 * else's. Exactly one of `text` and `error` is set; `error` is one short
 * plain sentence. Never broadcast, never stored. */
export interface AssistantAnswerMessage {
  readonly type: "assistantAnswer";
  readonly id: string;
  readonly text?: string;
  readonly error?: string;
}

/** To the host's sockets only: the agenda, what is covered, and the line to
 * say next. Never sent to anyone else, never stored. */
export interface AssistantHostMessage {
  readonly type: "assistantHost";
  readonly host: AssistantHostState;
}

/** To the host's sockets only, in a listen room: the newest transcript lines
 * (oldest first, capListenLines), sent whole each time a line is heard, since
 * a guest line can take back an operator line that was its echo. Never
 * broadcast, never stored in room state. */
export interface ListenLinesMessage {
  readonly type: "listenLines";
  readonly lines: readonly ListenLine[];
}

/** The answer to an `assistantTest` `peek`, to the asking socket only. */
export interface AssistantTestResultMessage {
  readonly type: "assistantTestResult";
  readonly result: unknown;
}

/** To a seat's old socket: the same account moved this seat to another
 * device (a join with `move=1`). The socket is closed with
 * `ROOM_CLOSE_CODES.movedAway` right after; the client must not reconnect,
 * and can offer to move the call back (join again with `move=1`). */
export interface MovedAwayMessage {
  readonly type: "movedAway";
}

/** To every socket when the owner finishes the call: it is over for
 * everyone. Sent just before the close with `ROOM_CLOSE_CODES.ended`, because
 * a close the Room starts can take the edge about 18 seconds to deliver
 * (QUEUE row 264), and the client ends on this message instead. */
export interface EndedMessage {
  readonly type: "ended";
}

export interface RoomFullMessage {
  readonly type: "roomFull";
  readonly maxParticipants: number;
}

/** To an approved socket, right after `welcome` and on a resync: the whole
 * chat so far. Never part of `RoomSnapshot`. */
export interface ChatStateMessage {
  readonly type: "chatState";
  readonly messages: readonly ChatMessage[];
}

/** To every approved socket: a chat message was sent. */
export interface ChatMessageMessage {
  readonly type: "chatMessage";
  readonly message: ChatMessage;
}

/** To every approved socket: a chat message was deleted. */
export interface ChatRemovedMessage {
  readonly type: "chatRemoved";
  readonly id: string;
}

export interface ErrorMessage {
  readonly type: "error";
  readonly code: string;
  readonly message: string;
}

export interface PongMessage {
  readonly type: "pong";
}

export type RoomMessage =
  | WelcomeMessage
  | WaitingMessage
  | KnockWaitingMessage
  | KnockCancelledMessage
  | KnockResultMessage
  | ParticipantJoinedMessage
  | ParticipantLeftMessage
  | ParticipantUpdatedMessage
  | RecordingStateMessage
  | RecordingDiscardedMessage
  | PanelStateMessage
  | PanelItemMessage
  | PanelRemovedMessage
  | ChatStateMessage
  | ChatMessageMessage
  | ChatRemovedMessage
  | PrivateStateMessage
  | PrivateSavedMessage
  | SuggestionsMessage
  | PrepEditedMessage
  | LateGuestMessage
  | AssistantStateMessage
  | AssistantAnswerMessage
  | AssistantHostMessage
  | ListenLinesMessage
  | AssistantTestResultMessage
  | MovedAwayMessage
  | EndedMessage
  | RoomFullMessage
  | ErrorMessage
  | PongMessage;

export function isClientMessage(value: unknown): value is ClientMessage {
  return (
    typeof value === "object" &&
    value !== null &&
    "type" in value &&
    typeof (value as { type: unknown }).type === "string"
  );
}
